import {fixNumbers, isValidNationalCode} from '../../../../../../_metronic/helpers'

export type BuyerIdentifierErrors = {
  nationalCode?: string
  economicCode?: string
}

export const normalizeBuyerIdentifier = (value?: string) =>
  fixNumbers(value || '').replace(/[\s\-_/\.\u200c\u200d\u200e\u200f]/g, '')

const isDigits = (value: string) => /^\d+$/.test(value)

export const foreignIdentifierMessage =
  'شناسه خریدار غیرایرانی باید کد فراگیر ۱۲ رقمی یا شماره گذرنامه (حداکثر ۹ حرف و رقم لاتین) باشد.'

// A foreign buyer without a FIDA code is identified by passport (header.bpn, currency-sale
// invoices only). Mirrors InvoiceBuyerIdentityPolicy.NormalizePassport on the backend.
export const isForeignPassport = (value?: string) =>
  /^[A-Za-z0-9]{1,9}$/.test(normalizeBuyerIdentifier(value))

export const validateBuyerIdentifiers = (
  taxpayerType: number,
  nationalCodeValue?: string,
  economicCodeValue?: string
): BuyerIdentifierErrors => {
  const nationalCode = normalizeBuyerIdentifier(nationalCodeValue)
  const economicCode = normalizeBuyerIdentifier(economicCodeValue)
  const errors: BuyerIdentifierErrors = {}
  const foreignPassport = taxpayerType === 4 && isForeignPassport(nationalCode)

  if (nationalCode && !foreignPassport && !isDigits(nationalCode)) {
    errors.nationalCode =
      taxpayerType === 4 ? foreignIdentifierMessage : 'شناسه هویتی فقط می‌تواند شامل رقم باشد.'
  }
  if (economicCode && !isDigits(economicCode)) {
    errors.economicCode = 'شماره اقتصادی فقط می‌تواند شامل رقم باشد.'
  }
  if (errors.nationalCode || errors.economicCode) return errors

  if (taxpayerType === 1) {
    if (nationalCode && (nationalCode.length !== 10 || !isValidNationalCode(nationalCode))) {
      errors.nationalCode = 'کد ملی خریدار حقیقی معتبر نیست.'
    }
    if (economicCode && economicCode.length !== 14) {
      errors.economicCode = 'شماره اقتصادی خریدار حقیقی باید دقیقاً ۱۴ رقم باشد.'
    } else if (nationalCode && economicCode && !economicCode.startsWith(nationalCode)) {
      errors.economicCode = '۱۰ رقم نخست شماره اقتصادی باید با کد ملی یکسان باشد.'
    }
  } else if (taxpayerType === 2) {
    // A legal شناسه ملی is 11 digits and equal to the economic code — nothing more.
    if (nationalCode && nationalCode.length !== 11) {
      errors.nationalCode = 'شناسه ملی خریدار حقوقی باید دقیقاً ۱۱ رقم باشد.'
    }
    if (economicCode && economicCode.length !== 11) {
      errors.economicCode = 'شماره اقتصادی خریدار حقوقی باید دقیقاً ۱۱ رقم باشد.'
    } else if (nationalCode && economicCode && nationalCode !== economicCode) {
      errors.economicCode = 'شماره اقتصادی خریدار حقوقی باید با شناسه ملی یکسان باشد.'
    }
  } else if (taxpayerType === 3) {
    if (nationalCode && nationalCode.length !== 10) {
      errors.nationalCode = 'شناسه مشارکت مدنی باید دقیقاً ۱۰ رقم باشد.'
    }
    if (economicCode && economicCode.length !== 11) {
      errors.economicCode = 'شماره اقتصادی مشارکت مدنی باید دقیقاً ۱۱ رقم باشد.'
    }
  } else if (taxpayerType === 4) {
    if (nationalCode && !foreignPassport && nationalCode.length !== 12) {
      errors.nationalCode = foreignIdentifierMessage
    }
    if (economicCode && economicCode.length !== 14) {
      errors.economicCode = 'شماره اقتصادی خریدار غیرایرانی باید دقیقاً ۱۴ رقم باشد.'
    }
  }

  return errors
}

export type BuyerContactErrors = {
  zipCode?: string
  mobile?: string
}

/**
 * The postal code and mobile as the server accepts them (BuyerListRules.ValidateContact): a malformed one is refused
 * with its reason. They used to be dropped silently, so the buyer was saved without them.
 */
export const validateBuyerContact = (zipCodeValue?: string, mobileValue?: string): BuyerContactErrors => {
  const zipCode = normalizeBuyerIdentifier(zipCodeValue)
  const mobile = normalizeBuyerIdentifier(mobileValue)
  const errors: BuyerContactErrors = {}
  if (zipCode && !/^\d{10}$/.test(zipCode)) {
    errors.zipCode = 'کد پستی باید دقیقاً ۱۰ رقم باشد.'
  }
  if (mobile && !/^0?9\d{9}$/.test(mobile)) {
    errors.mobile = 'شماره موبایل باید ۱۱ رقم باشد و با ۰۹ شروع شود.'
  }
  return errors
}

export const getBuyerIdentifierMaxLength = (taxpayerType: number) =>
  taxpayerType === 2 ? 11 : taxpayerType === 4 ? 12 : 10

export const getBuyerEconomicCodeLength = (taxpayerType?: number) =>
  taxpayerType === 1 || taxpayerType === 4
    ? 14
    : taxpayerType === 2 || taxpayerType === 3
    ? 11
    : undefined
